/**
 * EffectiveSLP — kid-facing practice player scoring-rubric schemas
 * and validators (fix #252 / #PL-WEB-RUBRICS).
 *
 * The five practice archetypes share a common discriminator:
 * `scoring_rubric.archetype` (see `Archetype`). Each archetype has
 * its own TypeScript interface below plus a runtime validator
 * (`isValid<Archetype>Rubric`) that the player calls BEFORE it
 * decides which renderer to mount. A malformed rubric falls back
 * to the "This activity isn't set up yet" screen (AC #5).
 *
 * Five archetypes:
 *   1. `picture_naming`        — image + "Say it" + 4 tap-word choices
 *      (Shipped in #251. Schema defined here so the dispatcher can
 *      discriminate it; the existing inline payload shape is a
 *      function-superset.)
 *   2. `receptive_directions`  — 3x3 grid, tap-sequence (#252 AC #2)
 *   3. `sentence_completion`   — drag word from bank into blank (#252)
 *   4. `conversation_turns`    — 3-tap multiple choice (#252)
 *   5. `sequencing`            — drag-to-reorder 4 cards (#252)
 *
 * Self-correction windows (AC #4 in the issue body):
 *   - picture_naming: 3s   (re-tap a different word choice)
 *   - receptive_directions: 4s (re-tap a different tile)
 *   - sentence_completion: drag back to bank + re-drag
 *   - conversation_turns: 4s (tap a different option)
 *   - sequencing: drag/re-drag within trial
 *
 * All windows are exposed as optional `self_correct_window_ms`
 * fields on the rubric so v1.1 can A/B test different values
 * without a code change (the issue's Risks section calls this
 * out explicitly).
 *
 * The `clinician_reviewed` flag is the gating risk-mitigation
 * from the issue body: until an SLP validates a rubric in #252's
 * sibling, its scores are treated as a "trend signal", not an
 * authoritative measurement.
 */

export type Archetype =
  | 'picture_naming'
  | 'receptive_directions'
  | 'sentence_completion'
  | 'conversation_turns'
  | 'sequencing';

export const ARCHETYPES: readonly Archetype[] = [
  'picture_naming',
  'receptive_directions',
  'sentence_completion',
  'conversation_turns',
  'sequencing',
] as const;

/**
 * A single tile in a `receptive_directions` 3x3 grid. Empty
 * strings represent blank/wall tiles — the kid cannot tap into
 * them. The validator rejects tiles that are not exactly one of
 * shape/color/emoji -tagged.
 */
/**
 * 3x3 grid tap-sequence (#252 AC #2, receptive_directions).
 * The clinician-supplied prompt describes the order the kid
 * should tap (e.g. "Touch the red one, then the circle, then
 * the star"). The renderer plays `prompt_audio` once on mount,
 * shows the 3x3 grid of tiles, and records the kid's tap
 * sequence.
 *
 * Per the issue spec, `grid` is a 9-item array and
 * `expected_sequence` is an ordered list of 0-based grid indices.
 */
export interface ReceptiveDirectionsRubric {
  archetype: 'receptive_directions';
  /** Audio URL spoken on trial start ("Touch red, then circle…"). */
  prompt_audio: string;
  /** Display title — shown to the clinician only in authoring. */
  title?: string;
  /** Exactly 9 tiles in display order (row-major 3x3). */
  grid: string[];
  /** Ordered 0-based grid indices the kid is expected to tap. */
  expected_sequence: number[];
  /** Self-correction window for re-tapping a different tile. Default 4000. */
  self_correct_window_ms?: number;
  /** Whether an SLP has reviewed this rubric yet. Default false. */
  clinician_reviewed?: boolean;
}

/**
 * `sentence_completion` — drag-into-blank (#252 AC #2). The kid
 * sees a scenario image + 3 word choices (the bank) and drags
 * each one into a single blank in the scenario sentence, in
 * order. `expected` is the ordered array of words that complete
 * the sentence; `alternates` is an optional per-slot list of
 * synonyms that also count as correct (so "colour" and "color"
 * both score).
 */
export interface SentenceCompletionRubric {
  archetype: 'sentence_completion';
  scenario_image_url: string;
  scenario_audio: string;
  prompt_audio: string;
  title?: string;
  /** Three draggable word chips shown in the bank. */
  word_bank: string[];
  /** Correct words in slot order (one per blank). */
  expected: string[];
  /** Per-slot alternative acceptable words (case-insensitive). */
  alternates?: string[][];
  self_correct_window_ms?: number;
  clinician_reviewed?: boolean;
}

/**
 * `conversation_turns` — 3-tap multiple choice (#252 AC #2). The
 * kid listens to a scenario (image + audio), then taps one of
 * three response options. Each option is tagged with a clinical
 * tier:
 *   - `correct` — full credit
 *   - `partial` — half credit (clinician decides what counts)
 *   - `wrong`   — no credit
 *
 * AC #4 in the issue body: tap a different option within 4s to
 * flag `self_corrected`.
 */
export type ConversationTurnsTag = 'correct' | 'partial' | 'wrong';

export interface ConversationTurnsOption {
  text: string;
  tag: ConversationTurnsTag;
}

export interface ConversationTurnsRubric {
  archetype: 'conversation_turns';
  scenario_image_url: string;
  scenario_audio: string;
  title?: string;
  /** Exactly 3 response options (one per tier — see AC #2). */
  options: ConversationTurnsOption[];
  self_correct_window_ms?: number;
  clinician_reviewed?: boolean;
}

/**
 * One card in a `sequencing` task (#252 AC #2). 4 cards total.
 * `position` is the canonical (correct) 0-indexed slot.
 * `image_url` is what the kid sees. The renderer shows the 4
 * cards in a random order; the kid reorders them by drag-or-tap.
 * Scoring: positional permutation match against `position`,
 * with optional `one_swap_tolerance` (swapping two adjacent
 * cards still counts as correct — this is a UX leniency, NOT
 * a clinical one, per the issue body's Risks section).
 */
export interface SequencingCard {
  image_url: string;
  /** Canonical (correct) 0-indexed slot this card belongs in. */
  position: number;
}

export interface SequencingRubric {
  archetype: 'sequencing';
  /** Exactly 4 cards (one per slot). */
  cards: SequencingCard[];
  /**
   * UX leniency (NOT clinical) — when true, swapping two adjacent
   * cards in the kid's arrangement still scores as a full pass.
   * Documented in code so future maintainers understand why it's
   * lenient by default (per the issue body's Risks section).
   */
  one_swap_tolerance: boolean;
  title?: string;
  self_correct_window_ms?: number;
  clinician_reviewed?: boolean;
}

/**
 * `picture_naming` rubric shape — defined here so the dispatcher
 * has a discriminated union, but the existing #251 renderer uses
 * the inline `PictureNamingTrial` shape built from `activity_payload.trials`.
 * The rubric is the SLP-curated anchor that points to the trial
 * pool; the renderer prefers `activity_payload.trials` when present.
 */
export interface PictureNamingRubric {
  archetype: 'picture_naming';
  title?: string;
  /** Optional reviewer flag. */
  clinician_reviewed?: boolean;
}

/** Union of all rubric shapes. */
export type PracticeRubric =
  | PictureNamingRubric
  | ReceptiveDirectionsRubric
  | SentenceCompletionRubric
  | ConversationTurnsRubric
  | SequencingRubric;

/* ------------------------------------------------------------------------- */
/* Type guards / validators                                                  */
/* ------------------------------------------------------------------------- */

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

function isOptionalNonEmptyString(v: unknown): v is string | undefined {
  return v === undefined || (typeof v === 'string' && v.length > 0);
}

function isStringArray(v: unknown): v is string[] {
  return (
    Array.isArray(v) &&
    v.every((x) => typeof x === 'string' && x.length > 0)
  );
}

function isOptionalBoolean(v: unknown): v is boolean | undefined {
  return v === undefined || typeof v === 'boolean';
}

function isOptionalPositiveInt(v: unknown): v is number | undefined {
  return (
    v === undefined || (typeof v === 'number' && Number.isInteger(v) && v >= 0)
  );
}

/**
 * Count case-insensitive occurrences of every string in `arr`. Used
 * by `isValidSentenceCompletionRubric` to enforce that every word
 * the kid is asked to place is backed by a sufficient bank supply
 * (fix #301). The renderer (SentenceCompletion.tsx) removes each
 * placed word from the bank by its index, so any bank entry can be
 * consumed at most once. If `expected` references a word more times
 * than `word_bank` supplies it, the trial becomes un-completable
 * and the "Got it!" button never appears — soft-locking the kid
 * on the activity. We reject those rubrics at validation time so
 * the dispatcher falls back to "activity isn't set up yet" instead
 * (see also the comment on `isValidSentenceCompletionRubric`).
 */
function countOccurrences(arr: readonly string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const w of arr) {
    const key = w.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/**
 * Narrow the top-level `scoring_rubric` to a `PracticeRubric`. The
 * `archetype` field is the discriminator; valid shape is enforced
 * below per archetype. Returns false for any unknown archetype
 * string (so the dispatcher can fall back to "not set up").
 */
export function isPracticeRubric(obj: unknown): obj is PracticeRubric {
  if (!isObject(obj)) return false;
  const arch = obj.archetype;
  switch (arch) {
    case 'picture_naming':
      return isValidPictureNamingRubric(obj);
    case 'receptive_directions':
      return isValidReceptiveDirectionsRubric(obj);
    case 'sentence_completion':
      return isValidSentenceCompletionRubric(obj);
    case 'conversation_turns':
      return isValidConversationTurnsRubric(obj);
    case 'sequencing':
      return isValidSequencingRubric(obj);
    default:
      return false;
  }
}

export function isValidPictureNamingRubric(
  obj: unknown,
): obj is PictureNamingRubric {
  if (!isObject(obj)) return false;
  if (obj.archetype !== 'picture_naming') return false;
  if (!isOptionalNonEmptyString(obj.title)) return false;
  if (!isOptionalBoolean(obj.clinician_reviewed)) return false;
  return true;
}

export function isValidReceptiveDirectionsRubric(
  obj: unknown,
): obj is ReceptiveDirectionsRubric {
  if (!isObject(obj)) return false;
  if (obj.archetype !== 'receptive_directions') return false;
  if (!isNonEmptyString(obj.prompt_audio)) return false;
  if (!isOptionalNonEmptyString(obj.title)) return false;
  if (!Array.isArray(obj.grid) || obj.grid.length !== 9) return false;
  for (const tile of obj.grid) {
    if (!isNonEmptyString(tile)) return false;
  }
  if (!Array.isArray(obj.expected_sequence) || obj.expected_sequence.length === 0) {
    return false;
  }
  const seen = new Set<number>();
  for (const idx of obj.expected_sequence) {
    if (
      typeof idx !== 'number' ||
      !Number.isInteger(idx) ||
      idx < 0 ||
      idx >= obj.grid.length ||
      seen.has(idx)
    ) {
      return false;
    }
    seen.add(idx);
  }
  if (!isOptionalPositiveInt(obj.self_correct_window_ms)) return false;
  if (!isOptionalBoolean(obj.clinician_reviewed)) return false;
  return true;
}

export function isValidSentenceCompletionRubric(
  obj: unknown,
): obj is SentenceCompletionRubric {
  if (!isObject(obj)) return false;
  if (obj.archetype !== 'sentence_completion') return false;
  if (!isNonEmptyString(obj.scenario_image_url)) return false;
  if (!isNonEmptyString(obj.scenario_audio)) return false;
  if (!isNonEmptyString(obj.prompt_audio)) return false;
  if (!isOptionalNonEmptyString(obj.title)) return false;
  if (!isStringArray(obj.word_bank) || obj.word_bank.length !== 3) return false;
  if (!Array.isArray(obj.expected) || obj.expected.length === 0) return false;
  if (obj.expected.length > obj.word_bank.length) return false;
  for (const w of obj.expected) {
    if (!isNonEmptyString(w)) return false;
  }
  // fix(#301) — reject rubrics whose `expected` references a word
  // more times than `word_bank` supplies it. The renderer
  // (SentenceCompletion.tsx) removes the placed word from the bank
  // by its index on each placeWord call, so every bank entry can
  // only be consumed once; a rubric like `word_bank: ['a','b','c']`
  // + `expected: ['a','a']` would validate today but is
  // un-completable (allPlaced never becomes true → "Got it!"
  // never appears → kid soft-locked on the trial). We require
  // per-word case-insensitive multiplicity in `expected` to be
  // <= the corresponding multiplicity in `word_bank`. `alternates`
  // are scoring synonyms — they're not in the bank and the kid
  // must still physically drag a bank word into the slot, so
  // alternates do NOT add to the multiplicity budget.
  const bankCounts = countOccurrences(obj.word_bank);
  const expectedCounts = countOccurrences(obj.expected);
  for (const [word, want] of expectedCounts) {
    const have = bankCounts.get(word) ?? 0;
    if (want > have) return false;
  }
  if (obj.alternates !== undefined) {
    if (!Array.isArray(obj.alternates)) return false;
    if (obj.alternates.length !== obj.expected.length) return false;
    for (const slotAlternates of obj.alternates) {
      if (!Array.isArray(slotAlternates)) return false;
      for (const w of slotAlternates) {
        if (!isNonEmptyString(w)) return false;
      }
    }
  }
  if (!isOptionalPositiveInt(obj.self_correct_window_ms)) return false;
  if (!isOptionalBoolean(obj.clinician_reviewed)) return false;
  return true;
}

export function isValidConversationTurnsRubric(
  obj: unknown,
): obj is ConversationTurnsRubric {
  if (!isObject(obj)) return false;
  if (obj.archetype !== 'conversation_turns') return false;
  if (!isNonEmptyString(obj.scenario_image_url)) return false;
  if (!isNonEmptyString(obj.scenario_audio)) return false;
  if (!isOptionalNonEmptyString(obj.title)) return false;
  if (!Array.isArray(obj.options) || obj.options.length !== 3) return false;
  const tagsSeen = new Set<ConversationTurnsTag>();
  for (const opt of obj.options) {
    if (!isObject(opt)) return false;
    if (!isNonEmptyString(opt.text)) return false;
    const tag = opt.tag;
    if (tag !== 'correct' && tag !== 'partial' && tag !== 'wrong') {
      return false;
    }
    tagsSeen.add(tag);
  }
  // AC #2 requires exactly one option per tier.
  if (
    !tagsSeen.has('correct') ||
    !tagsSeen.has('partial') ||
    !tagsSeen.has('wrong')
  ) {
    return false;
  }
  if (!isOptionalPositiveInt(obj.self_correct_window_ms)) return false;
  if (!isOptionalBoolean(obj.clinician_reviewed)) return false;
  return true;
}

export function isValidSequencingRubric(
  obj: unknown,
): obj is SequencingRubric {
  if (!isObject(obj)) return false;
  if (obj.archetype !== 'sequencing') return false;
  if (!Array.isArray(obj.cards) || obj.cards.length !== 4) return false;
  const positions = new Set<number>();
  for (const card of obj.cards) {
    if (!isObject(card)) return false;
    if (!isNonEmptyString(card.image_url)) return false;
    if (
      typeof card.position !== 'number' ||
      !Number.isInteger(card.position) ||
      card.position < 0 ||
      card.position > 3
    ) {
      return false;
    }
    if (positions.has(card.position)) return false;
    positions.add(card.position);
  }
  if (positions.size !== 4) return false;
  if (typeof obj.one_swap_tolerance !== 'boolean') return false;
  if (!isOptionalNonEmptyString(obj.title)) return false;
  if (!isOptionalPositiveInt(obj.self_correct_window_ms)) return false;
  if (!isOptionalBoolean(obj.clinician_reviewed)) return false;
  return true;
}

/* ------------------------------------------------------------------------- */
/* Defaults                                                                  */
/* ------------------------------------------------------------------------- */

/** Default self-correction window for the 4 new archetypes (4s per AC #2). */
export const DEFAULT_SELF_CORRECT_WINDOW_MS = 4_000;

/**
 * Default self-correction window for `picture_naming` (3s, per
 * the existing AC in #251 — preserved to avoid changing behavior
 * for shipped activities).
 */
export const DEFAULT_PICTURE_NAMING_SELF_CORRECT_WINDOW_MS = 3_000;
